import { Avatar } from '@socialchorus/shared-ui-components';

import './mention-avatar.scss';

/**
 * Per-user avatar colours come from `Advocate#avatar_color` in governor, which
 * is `colors[id % 9]` over a fixed eight-value palette. The design-system
 * Avatar has no colour prop by design — its initials fallback takes background
 * and text colour from `className` — so each palette entry maps to a class that
 * pairs the background with a text colour clearing WCAG AA (4.5:1). One fixed
 * text colour cannot: white fails on six of the eight, black fails on #ab47bc
 * (4.36:1).
 */
const AVATAR_COLOR_CLASSES: Record<string, string> = {
  '#7986cb': 'mention-avatar--indigo',
  '#ef5350': 'mention-avatar--red',
  '#26c6da': 'mention-avatar--cyan',
  '#66bb6a': 'mention-avatar--green',
  '#42a5f5': 'mention-avatar--blue',
  '#ec407a': 'mention-avatar--pink',
  '#cddc39': 'mention-avatar--lime',
  '#ab47bc': 'mention-avatar--purple',
};

function firstGrapheme(token: string): string {
  // Segmenter keeps emoji and combining marks intact; the spread is a fallback
  // for environments without it, and is still safer than String#charAt.
  if (typeof Intl !== 'undefined' && 'Segmenter' in Intl) {
    const segmenter = new Intl.Segmenter(undefined, {
      granularity: 'grapheme',
    });
    return [...segmenter.segment(token)][0]?.segment ?? '';
  }
  return [...token][0] ?? '';
}

/**
 * Initials for the avatar fallback, derived from the composed display name so
 * the Experience and Studio produce the same letters for the same user (both
 * read governor's preferred-name-aware `name`). Returns '' when nothing usable
 * can be derived, which makes the Avatar fall back to its placeholder image.
 *
 * A trailing parenthetical token, e.g. the comment picker's "John Smith
 * (you)", is dropped before deriving initials — otherwise the annotation
 * itself would be read as the last name.
 */
export function initialsFromName(name: string | undefined): string {
  const tokens = (name ?? '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .filter((token) => !/^\(.*\)$/.test(token));
  if (tokens.length === 0) return '';
  const first = firstGrapheme(tokens[0]);
  const last =
    tokens.length > 1 ? firstGrapheme(tokens[tokens.length - 1]) : '';
  return `${first}${last}`.toUpperCase();
}

export function avatarColorClass(avatarColor?: string | null): string {
  return (avatarColor && AVATAR_COLOR_CLASSES[avatarColor.toLowerCase()]) || '';
}

interface MentionAvatarProps {
  name: string;
  avatarUrl?: string | null;
  avatarColor?: string | null;
}

/**
 * The suggestion row's avatar: the user's image when they have one, otherwise
 * their initials on their avatar colour, otherwise the default placeholder.
 *
 * `alt=""` is deliberate and required. The design-system Avatar uses
 * `alt ?? initials` as the accessible name, so omitting it makes a screen
 * reader announce the initials *and* the user's name from the adjacent text.
 */
export function MentionAvatar({
  name,
  avatarUrl,
  avatarColor,
}: MentionAvatarProps) {
  const initials = initialsFromName(name);
  const showsInitials = !avatarUrl && initials !== '';

  return (
    <Avatar
      size="medium"
      shape="circle"
      imgSrc={avatarUrl || undefined}
      initials={initials}
      alt=""
      className={showsInitials ? avatarColorClass(avatarColor) : undefined}
    />
  );
}
